import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { PointsAuditApi } from '@/utilities/api/PointsAuditApi';
import { describeAuditEvent } from './describeAuditEvent';
import { describeProcessSource } from './describeProcessSource';
import { ProcessImpactTable } from './ProcessImpactTable';
import { RevertScopeList } from './RevertScopeList';
import { PointsAuditEndpoint } from '@api/endpoints';
import { ApiPointsAuditProcess } from '@api/interfaces';
import { PointsAuditRevertBlock } from '@shared/enums';
import { Alert, CircularProgress, Stack, TextField } from '@mui/material';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

/** Not translated: the check is on the exact word. */
const CONFIRM_WORD = 'REVERT';

/** The preview names the first few; the rest are counted. */
const outsiderNames = (
  preview: PointsAuditEndpoint.RevertPreview.Response,
  t: TFunction,
) => {
  const names = preview.outsideScope
    .map((farmer) => farmer.businessName || farmer.name || farmer.externalId)
    .join(', ');
  const unnamedCount = preview.outsideScopeCount - preview.outsideScope.length;
  return unnamedCount > 0
    ? t($ => $.pointsAudit.revert.namesAndMore, { count: unnamedCount, names })
    : names;
};

interface RevertProcessDialogProps {
  onClose: () => void;
  /** Opens another process's revert, such as the newer change that has to come off first. */
  onOpenProcess: (process: ApiPointsAuditProcess) => void;
  /** Called once the dialog is done, with what to tell the admin. */
  onReverted: (message: string) => void;
  process: ApiPointsAuditProcess;
}

/** A revert can't be undone, so this shows the real impact first and requires typing REVERT. */
export const RevertProcessDialog = ({
  onClose,
  onOpenProcess,
  onReverted,
  process,
}: RevertProcessDialogProps) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();

  const { data: detail } = useQuery(
    ['points-audit-process-detail', process.processId],
    () => PointsAuditApi.getProcess(process.processId),
    { retry: false },
  );

  const { data: preview, isFetching } = useQuery(
    ['points-audit-revert-preview', process.processId],
    () => PointsAuditApi.revertPreview(process.processId),
    { onError: (err: { message: string }) => setError(err.message), retry: false },
  );

  const blockedByProcessId = preview?.blockedByProcessId;
  const { data: newerChange } = useQuery(
    ['points-audit-process-detail', blockedByProcessId],
    () => PointsAuditApi.getProcess(blockedByProcessId ?? ''),
    { enabled: !!blockedByProcessId, retry: false },
  );

  const { isLoading, mutate: revert } = useMutation(
    () => PointsAuditApi.revert(process.processId, { reason: reason || undefined }),
    {
      onError: (err: { message: string }) => setError(err.message),
      onSuccess: () => onReverted(t($ => $.pointsAudit.revert.done)),
    },
  );

  const canConfirm = typed === CONFIRM_WORD
    && !!preview
    && !preview.isAlreadyReverted
    && !preview.revertBlock
    && !preview.outsideScope.length
    && preview.hasUndoableChanges;

  const blockMessage = () => {
    switch (preview?.revertBlock) {
      case PointsAuditRevertBlock.Locked:
        return t($ => $.pointsAudit.revert.locked);
      case PointsAuditRevertBlock.Recalculating:
        return t($ => $.pointsAudit.revert.recalculating);
      case PointsAuditRevertBlock.NewerChange:
        return newerChange
          ? t($ => $.pointsAudit.revert.newerChangeFirst, {
            change: describeProcessSource(newerChange.process, t),
            date: newerChange.process.lastEventAt,
          })
          : t($ => $.pointsAudit.revert.newerChangeFirstUnnamed);
      default:
        return undefined;
    }
  };

  const body = () => {
    if (isFetching) {
      return (
        <Stack alignItems="center" direction="row" gap="12px">
          <CircularProgress size={20} />
          <Text category="body-medium">{t($ => $.pointsAudit.revert.checking)}</Text>
        </Stack>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!preview) { return null; }

    const block = !preview.isAlreadyReverted && blockMessage();

    return (
      <Stack>
        {!!block && (
          <>
            <Alert
              action={newerChange && preview.revertBlock === PointsAuditRevertBlock.NewerChange ? (
                <Button
                  onClick={() => onOpenProcess(newerChange.process)}
                  size="small"
                  testID="revert-open-newer-change"
                  variant="text"
                >
                  {t($ => $.pointsAudit.revert.openNewerChange)}
                </Button>
              ) : undefined}
              data-testid={`revert-blocked-${preview.revertBlock}`}
              severity="error"
            >
              {block}
            </Alert>
            <VSpacer size="3" />
          </>
        )}

        {!preview.isAlreadyReverted && preview.outsideScope.length > 0 && (
          <>
            <Alert data-testid="revert-outside-scope" severity="error">
              {t($ => $.pointsAudit.revert.outsideScope, { names: outsiderNames(preview, t) })}
            </Alert>
            <VSpacer size="3" />
          </>
        )}

        <Text category="body-medium">
          {t($ => $.pointsAudit.revert.impact, {
            count: preview.farmersAffected,
            orders: preview.ordersToDemote,
            source: describeProcessSource(process, t),
          })}
        </Text>

        <VSpacer size="4" />

        {preview.isAlreadyReverted && (
          <Alert severity="warning">{t($ => $.pointsAudit.revert.alreadyReverted)}</Alert>
        )}

        {!preview.isAlreadyReverted && !preview.hasUndoableChanges && (
          <>
            <Alert severity="error">{t($ => $.pointsAudit.revert.nothingToUndo)}</Alert>
            <VSpacer size="3" />
          </>
        )}

        {preview.conflicts.length > 0 && (
          <>
            <Alert severity="info">
              {t($ => $.pointsAudit.revert.conflicts, { count: preview.conflicts.length })}
            </Alert>
            <VSpacer size="3" />
          </>
        )}

        {!!detail?.events.length && (
          <>
            <Text category="label-medium">{t($ => $.pointsAudit.revert.whatThisDid)}</Text>
            <VSpacer size="2" />
            {[...new Set(detail.events.map((row) => describeAuditEvent(row, t)))]
              .slice(0, 5)
              .map((sentence) => (
                <Text category="body-medium" key={sentence}>{sentence}</Text>
              ))}
            <VSpacer size="4" />
          </>
        )}

        {!!preview.scope && <RevertScopeList scope={preview.scope} />}

        {!!detail?.impact.length && (
          <>
            <Text category="label-medium">{t($ => $.pointsAudit.revert.affectedFarmers)}</Text>
            <VSpacer size="2" />
            <ProcessImpactTable impact={detail.impact} />
            <VSpacer size="4" />
          </>
        )}

        <Alert severity="error">{t($ => $.pointsAudit.revert.cannotBeUndone)}</Alert>

        <VSpacer size="5" />

        <TextField
          fullWidth
          inputProps={{ 'data-testid': 'revert-reason-input' }}
          label={t($ => $.pointsAudit.revert.reasonLabel)}
          onChange={(event) => setReason(event.target.value)}
          value={reason}
        />

        <VSpacer size="4" />

        <TextField
          fullWidth
          inputProps={{ 'data-testid': 'revert-confirm-input' }}
          label={t($ => $.pointsAudit.revert.typeToConfirm, { word: CONFIRM_WORD })}
          onChange={(event) => setTyped(event.target.value)}
          value={typed}
        />
      </Stack>
    );
  };

  return (
    <ConfirmDialog
      cancelText={t($ => $.common.actions.cancel)}
      confirmText={t($ => $.pointsAudit.revert.confirm)}
      disableClose={isLoading}
      isLoading={isLoading}
      onCancel={onClose}
      onConfirm={canConfirm ? () => revert() : undefined}
      open
      testID="revert-process-dialog"
      title={t($ => $.pointsAudit.revert.title)}
    >
      {body()}
    </ConfirmDialog>
  );
};
